import React, { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Chip, Box, CircularProgress,
  FormControlLabel, Switch, Select, MenuItem, FormControl, InputLabel, Typography,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import api from "../../services/api";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

// Retorna a datetime-local string no formato que o input espera (sem segundos)
const toDatetimeLocal = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultScheduledAt = () => {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return toDatetimeLocal(d);
};

/**
 * EmailComposeModal — componente reutilizável para compor e enviar/agendar e-mails.
 *
 * Props:
 *   open          {bool}    — controla visibilidade
 *   onClose       {fn}      — chamado ao fechar
 *   initialTo     {string}  — e-mail do destinatário pré-preenchido
 *   initialSubject{string}  — assunto pré-preenchido
 *   initialBody   {string}  — corpo pré-preenchido (HTML)
 *   onSent        {fn}      — callback após envio ou agendamento bem-sucedido
 */
const EmailComposeModal = ({ open, onClose, initialTo = "", initialSubject = "", initialBody = "", onSent }) => {
  const fileInputRef = useRef(null);

  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [attachments, setAttachments] = useState([]);

  const [scheduling, setScheduling] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(defaultScheduledAt());

  const [sending, setSending] = useState(false);

  // Sincroniza props iniciais quando o modal abre
  useEffect(() => {
    if (open) {
      setTo(initialTo || "");
      setSubject(initialSubject || "");
      setBody(initialBody || "");
      setAttachments([]);
      setScheduling(false);
      setScheduledAt(defaultScheduledAt());
    }
  }, [open, initialTo, initialSubject, initialBody]);

  // Carrega contas de email disponíveis
  useEffect(() => {
    if (!open) return;
    setLoadingAccounts(true);
    api.get("/email-accounts")
      .then(({ data }) => {
        const connected = (data || []).filter(a => a.status === "CONNECTED");
        setAccounts(connected);
        if (connected.length > 0) {
          const def = connected.find(a => a.isDefault) || connected[0];
          setAccountId(def.id);
        } else {
          setAccountId("");
        }
      })
      .catch(() => toast.error("Erro ao carregar contas de email"))
      .finally(() => setLoadingAccounts(false));
  }, [open]);

  const handleSend = async () => {
    if (!to.trim()) { toast.error("Informe o destinatário"); return; }
    if (!subject.trim()) { toast.error("Informe o assunto"); return; }
    if (!accountId) { toast.error("Nenhuma conta de email conectada"); return; }
    if (scheduling && !scheduledAt) { toast.error("Informe a data/hora de agendamento"); return; }

    setSending(true);
    try {
      if (scheduling) {
        await api.post(`/email-accounts/${accountId}/emails/schedule`, {
          to: to.trim(),
          subject: subject.trim(),
          html: body,
          scheduledAt,
        });
        toast.success("Email agendado com sucesso!");
      } else {
        const formData = new FormData();
        formData.append("to", to.trim());
        formData.append("subject", subject.trim());
        formData.append("html", body);
        attachments.forEach(f => formData.append("attachments", f));
        await api.post(`/email-accounts/${accountId}/emails/send`, formData);
        toast.success("Email enviado!");
      }
      onClose();
      if (onSent) onSent();
    } catch {
      toast.error(scheduling ? "Erro ao agendar email" : "Erro ao enviar email");
    } finally {
      setSending(false);
    }
  };

  const selectedAccount = accounts.find(a => a.id === accountId);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle style={{ background: "#4299e1", color: "#fff", fontWeight: 700, padding: "14px 20px" }}>
        {scheduling ? "Agendar Email" : "Novo Email"}
      </DialogTitle>

      <DialogContent style={{ paddingTop: 16 }}>
        {loadingAccounts ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
        ) : accounts.length === 0 ? (
          <Box py={2} textAlign="center">
            <Typography color="error">Nenhuma conta de email conectada.</Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>Vá em Configurações → Canais para conectar uma conta.</Typography>
          </Box>
        ) : (
          <>
            {/* Seletor de conta */}
            {accounts.length > 1 && (
              <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 12 }}>
                <InputLabel>Enviar de</InputLabel>
                <Select value={accountId} onChange={e => setAccountId(e.target.value)} label="Enviar de">
                  {accounts.map(a => (
                    <MenuItem key={a.id} value={a.id}>{a.name} — {a.email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {accounts.length === 1 && selectedAccount && (
              <Typography variant="caption" style={{ color: "#718096", display: "block", marginBottom: 10 }}>
                De: {selectedAccount.name} &lt;{selectedAccount.email}&gt;
              </Typography>
            )}

            <TextField label="Para" fullWidth variant="outlined" size="small" style={{ marginBottom: 10 }}
              value={to} onChange={e => setTo(e.target.value)} />
            <TextField label="Assunto" fullWidth variant="outlined" size="small" style={{ marginBottom: 10 }}
              value={subject} onChange={e => setSubject(e.target.value)} />

            <Box style={{ marginBottom: 10 }}>
              <ReactQuill
                theme="snow"
                value={body}
                onChange={setBody}
                style={{ height: 220, marginBottom: 42 }}
                modules={QUILL_MODULES}
              />
            </Box>

            {/* Anexos */}
            {!scheduling && (
              <Box style={{ marginTop: 8 }}>
                <input type="file" multiple ref={fileInputRef} style={{ display: "none" }}
                  onChange={e => { e.persist(); setAttachments(prev => [...prev, ...Array.from(e.target.files)]); }} />
                <Button size="small" startIcon={<AttachFileIcon />} onClick={() => fileInputRef.current?.click()} style={{ color: "#4299e1" }}>
                  Anexar arquivo
                </Button>
                {attachments.length > 0 && (
                  <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginTop: 6 }}>
                    {attachments.map((f, i) => (
                      <Chip key={i} label={`${f.name} (${(f.size / 1024).toFixed(0)}KB)`} size="small"
                        onDelete={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                        style={{ backgroundColor: "#ebf8ff", color: "#2b6cb0" }} />
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Agendamento */}
            <Box style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <FormControlLabel
                control={<Switch checked={scheduling} onChange={e => setScheduling(e.target.checked)} color="primary" />}
                label={<Box display="flex" alignItems="center" style={{ gap: 4 }}><ScheduleIcon style={{ fontSize: 18 }} /> Agendar envio</Box>}
              />
              {scheduling && (
                <TextField
                  type="datetime-local"
                  size="small"
                  variant="outlined"
                  label="Data e hora"
                  InputLabelProps={{ shrink: true }}
                  value={scheduledAt}
                  onChange={e => setScheduledAt(e.target.value)}
                  inputProps={{ min: toDatetimeLocal(new Date()) }}
                  style={{ minWidth: 220 }}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} /> : (scheduling ? <ScheduleIcon /> : <SendIcon />)}
          onClick={handleSend}
          disabled={sending || accounts.length === 0}
          style={{ backgroundColor: "#4299e1", color: "#fff" }}
        >
          {scheduling ? "Agendar" : "Enviar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailComposeModal;
